import { useState, ClipboardEvent, useCallback, ChangeEvent } from 'react';

import { listGet, toTruthyList } from 'utils/array';

interface FileInputOps {
  /**
   * Defaults to 5
   */
  limit?: number;

  enabled?: boolean;
}

export interface FileInput {
  file: File;
  url: string;
}

export const USE_FILE_LIMIT = 5;

export function useFileInput({ limit = USE_FILE_LIMIT, enabled = true }: FileInputOps = {}) {
  const [files, setFiles] = useState<FileInput[]>([]);

  const onPaste = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>) => {
      if (!enabled) return;

      const newFiles = toTruthyList(
        Array.from(event.clipboardData.items)
          .filter((x) => x.kind === 'file')
          .map((x) => x.getAsFile()),
      );

      if (newFiles.length) event.preventDefault();

      setFiles((old) =>
        [
          ...old,
          ...newFiles
            .filter((f) => !listGet(old, f.size, ({ file }) => file.size))
            .map((file) => ({ file, url: window.URL.createObjectURL(file) })),
        ].slice(0, limit),
      );
    },
    [limit, enabled],
  );

  const removeFile = useCallback((index: number) => {
    setFiles((old) => old.filter((_, i) => i !== index));
  }, []);

  const onFileInput = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      setFiles((old) =>
        [
          ...old,
          ...Array.from(target.files || [])
            .filter((f) => !listGet(old, f.size, ({ file }) => file.size))
            .map((file) => ({ file, url: window.URL.createObjectURL(file) })),
        ].slice(0, limit),
      );
    },
    [limit],
  );

  const clearFiles = useCallback(() => setFiles([]), []);

  return {
    files,
    onPaste,
    onFileInput,
    removeFile,
    clearFiles,
    fileLimitReached: files.length >= limit,
  };
}

export type UseFileInput = ReturnType<typeof useFileInput>;
